import * as React from 'react'
import { cn } from '../../lib/utils'

interface CubeLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
    size?: 'xs' | 'sm' | 'default' | 'lg'
    fullScreen?: boolean
}

const CubeLoader = React.forwardRef<HTMLDivElement, CubeLoaderProps>(
    ({ className, size = 'default', fullScreen = false, ...props }, ref) => {
        const fullScreenStyles: React.CSSProperties = {}

        if (fullScreen) {
            fullScreenStyles.minHeight = '85vh'
            fullScreenStyles.maxHeight = '85vh'
        }

        const sizeClasses = {
            xs: 'tw:scale-[0.2]',
            sm: 'tw:scale-[0.3]',
            default: 'tw:scale-[0.4]',
            lg: 'tw:scale-[0.5]',
        }

        const cubes = [
            { id: 1, className: 'cube_count_1', zIndex: 2, left: '50px', top: '0px', delay: '1s' },
            { id: 2, className: 'cube_count_2', zIndex: 1, left: '-16px', top: '38px', delay: '1.1s' },
            { id: 3, className: 'cube_count_3', zIndex: 1, left: '116px', top: '38px', delay: '1.2s' },
            { id: 4, className: 'cube_count_4', zIndex: 2, left: '-16px', top: '116px', delay: '1.1s' },
            { id: 5, className: 'cube_count_5', zIndex: 1, left: '116px', top: '116px', delay: '1.2s' },
            { id: 6, className: 'cube_count_6', zIndex: 0, left: '50px', top: '154px', delay: '1.3s' },
        ]

        const faces = ['front', 'back', 'right', 'left', 'top', 'bottom'] as const

        const getFaceTransform = (face: (typeof faces)[number]): string => {
            switch (face) {
                case 'front':
                    return 'rotateY(0deg) translateZ(50px)'
                case 'right':
                    return 'rotateY(90deg) translateZ(50px)'
                case 'back':
                    return 'rotateY(180deg) translateZ(50px)'
                case 'left':
                    return 'rotateY(-90deg) translateZ(50px)'
                case 'top':
                    return 'rotateX(90deg) translateZ(50px)'
                case 'bottom':
                    return 'rotateX(-90deg) translateZ(50px)'
            }
        }

        const getFaceBoxShadow = (face: (typeof faces)[number]): string => {
            switch (face) {
                case 'front':
                    return 'inset -6px -6px 0 0 var(--edge-color)'
                case 'left':
                    return 'inset 6px -6px 0 0 var(--edge-color)'
                case 'top':
                    return 'inset -6px 6px 0 0 var(--edge-color)'
                default:
                    return 'none'
            }
        }

        return (
            <div
                ref={ref}
                className={cn('tw:relative tw:flex tw:items-center tw:justify-center', className)}
                style={
                    {
                        '--anim-duration': '1.25s',
                        '--cube-fill': '#0D2245',
                        '--edge-color': '#2450BF',
                        ...fullScreenStyles,
                    } as React.CSSProperties
                }
                {...props}
            >
                <div
                    className={cn('scene', sizeClasses[size])}
                    style={{
                        width: '200px',
                        height: '250px',
                        position: 'relative',
                    }}
                >
                    {cubes.map((cube) => (
                        <div
                            key={cube.id}
                            className={`cube ${cube.className}`}
                            style={{
                                width: '100px',
                                height: '100px',
                                position: 'absolute',
                                zIndex: cube.zIndex,
                                left: cube.left,
                                top: cube.top,
                                transform: 'rotateX(-36deg) rotateY(45deg)',
                                transformStyle: 'preserve-3d',
                                animation: `cube var(--anim-duration) ${cube.delay} ease infinite`,
                            }}
                        >
                            {faces.map((face) => (
                                <div
                                    key={face}
                                    className={`cube__face cube__face--${face}`}
                                    style={{
                                        width: '100px',
                                        height: '100px',
                                        position: 'absolute',
                                        background: 'var(--cube-fill)',
                                        border: '6px solid var(--edge-color)',
                                        animation: `cube-face-${face} var(--anim-duration) ease infinite`,
                                        animationDelay: cube.delay,
                                        transform: getFaceTransform(face),
                                        boxShadow: getFaceBoxShadow(face),
                                    }}
                                />
                            ))}
                        </div>
                    ))}
                </div>

                <style>{`
                    @keyframes cube {
                        0% {
                            transform: rotateX(-36deg) rotateY(45deg) rotateZ(0);
                        }
                        100% {
                            transform: rotateX(-126deg) rotateY(0deg) rotateZ(-45deg);
                        }
                    }

                    @keyframes cube-face-front {
                        0% {
                            box-shadow: inset -6px -6px 0 0 var(--edge-color);
                        }
                        100% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                    }

                    @keyframes cube-face-right {
                        0% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                        50% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                        100% {
                            box-shadow: inset 6px -6px 0 0 var(--edge-color);
                        }
                    }

                    @keyframes cube-face-back {
                        0% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                        100% {
                            box-shadow: inset -6px -6px 0 0 var(--edge-color);
                        }
                    }

                    @keyframes cube-face-left {
                        0% {
                            box-shadow: inset 6px -6px 0 0 var(--edge-color);
                        }
                        100% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                    }

                    @keyframes cube-face-top {
                        0% {
                            box-shadow: inset -6px 6px 0 0 var(--edge-color);
                        }
                        50% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                        100% {
                            box-shadow: inset 6px -6px 0 0 var(--edge-color);
                        }
                    }

                    @keyframes cube-face-bottom {
                        0% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                        100% {
                            box-shadow: inset 0 0 0 0 var(--edge-color);
                        }
                    }
                `}</style>
            </div>
        )
    }
)

CubeLoader.displayName = 'CubeLoader'

export { CubeLoader }
